<template>
    <div class="site-detail-dashboard">
        <!-- Stats Cards Grid -->
        <div class="dashboard-grid">
            <!-- Site Status Card -->
            <DashboardCard 
                icon="dashboard" 
                title="Site Durumu"
                class="status-card"
            >
                <a-switch 
                    v-model="localEnable"
                    checked-children="Aktif"
                    un-checked-children="Pasif"
                    size="large"
                />
            </DashboardCard>

            <!-- Agents Count -->
            <DashboardCard 
                icon="team" 
                title="Ajanlar"
            >
                <div class="metric-value">{{ site.agentCount }}</div>
            </DashboardCard>

            <!-- Domain Count -->
            <DashboardCard 
                icon="global" 
                title="Domain Sayısı"
            >
                <div class="metric-value">{{ site.integratedOrigins.length }}</div>
            </DashboardCard>

            <!-- Realtime Visitors -->
            <DashboardCard 
                icon="eye" 
                title="Anlık Ziyaretçi"
                highlight
            >
                <div class="metric-value realtime">{{ site.realtimeVisitorCount || 0 }}</div>
            </DashboardCard>

            <!-- Integration Status -->
            <DashboardCard 
                icon="api" 
                title="Entegrasyon"
            >
                <a-badge 
                    :status="site.isIntegrated ? 'success' : 'error'"
                    :text="site.isIntegrated ? 'Aktif' : 'Pasif'"
                />
            </DashboardCard>

            <!-- Last Activity -->
            <DashboardCard 
                icon="clock-circle" 
                title="Son Aktivite"
            >
                <div class="metric-text">{{ lastActivity() }}</div>
            </DashboardCard>

            <!-- Suspend Status Card -->
            <DashboardCard 
                icon="lock" 
                title="Askıya Alma"
                class="suspend-card"
                :class="{ 'is-suspended': site.suspend }"
            >
                <a-switch 
                    v-model="localSuspend"
                    checked-children="Aktif"
                    un-checked-children="Pasif"
                    size="large"
                />
                <div v-if="site.suspendReason" class="suspend-reason">
                    <a-tag color="red">{{ getSuspendReasonText(site.suspendReason) }}</a-tag>
                </div>
            </DashboardCard>

            <!-- Partner Info -->
            <DashboardCard 
                icon="solution" 
                title="Partner"
            >
                <div class="metric-text">{{ $_.get(site, 'partner.name') || 'Partner Yok' }}</div>
            </DashboardCard>

            <!-- Blink -->
            <DashboardCard 
                icon="notification" 
                title="Blink"
                class="blink-card"
            >
                <a-switch 
                    v-model="localBlink"
                    checked-children="Aktif"
                    un-checked-children="Pasif"
                    size="large"
                />
            </DashboardCard>
        </div>

        <!-- Billing & Settings Section -->
        <div class="settings-section">
            <div class="section-header">
                <a-icon type="dollar" />
                <h3>Faturalandırma ve Ayarlar</h3>
            </div>

            <div class="settings-grid">
                <!-- Trial Days -->
                <SettingCard 
                    icon="calendar"
                    title="Trial Süresi"
                    :value="`${trialDay} Gün`"
                    :sub-value="`${getRemainingTrialDay()} Gün Kaldı`"
                    @edit="showTrialPrompt"
                />

                <!-- Payment Method -->
                <SettingCard 
                    icon="credit-card"
                    title="Ödeme Yöntemi"
                    class="payment-method-card"
                >
                    <a-radio-group v-model="localPaymentMethod" button-style="solid">
                        <a-radio-button value="credit-card">Kredi Kartı</a-radio-button>
                        <a-radio-button value="eft">EFT/Havale</a-radio-button>
                        <a-radio-button value="btc">BTC</a-radio-button>
                    </a-radio-group>
                </SettingCard>

                <!-- Total Discount -->
                <SettingCard 
                    icon="percentage"
                    title="Genel İndirim"
                    :value="`${totalDiscount}%`"
                    @edit="showTotalDiscountPrompt"
                />

                <!-- Invoice Due Days -->
                <SettingCard 
                    icon="calendar"
                    title="Vade Gün Sayısı"
                    :value="invoiceDueDays || 'Varsayılan (7)'"
                    @edit="showInvoiceDueDaysPrompt"
                />

                <!-- Whatsapp Wallet -->
                <SettingCard 
                    icon="wallet"
                    title="Whatsapp Cüzdan"
                    :value="`${whatsappWallet} TL`"
                    @edit="showWhasappWalletPrompt"
                />

                <!-- Whatsapp Low Limit -->
                <SettingCard 
                    icon="warning"
                    title="Whatsapp Alt Limit"
                    :value="`${maxLowBalance} TL`"
                    @edit="showWhasappLowLimitPrompt"
                />

                <!-- Monthly Mail Send Limit -->
                <SettingCard 
                    icon="mail"
                    title="Aylık Mail Limiti"
                    :value="`${monthlyMailSendLimit}`"
                    @edit="showMonthlyMailSendLimitPrompt"
                />
            </div>
        </div>

        <!-- Commission Settings Section -->
        <div class="settings-section">
            <div class="section-header">
                <a-icon type="percentage" />
                <h3>Komisyon Ayarları</h3>
            </div>

            <div class="settings-grid">
                <!-- Gupshup Commission -->
                <SettingCard 
                    icon="whats-app"
                    title="Whatsapp Business Komisyon"
                    :value="`${gupshupCommission}%`"
                    @edit="showGupshupCommissionPrompt"
                />

                <!-- Whatsapp Commission -->
                <SettingCard 
                    icon="message"
                    title="Whatsapp Komisyon"
                    :value="`${whatsappCommission}%`"
                    @edit="showWhatsappCommissionPrompt"
                />

                <!-- OpenAI Commission -->
                <SettingCard 
                    icon="robot"
                    title="OpenAI Komisyon"
                    :value="`${aiUsageCommission}%`"
                    @edit="showOpenAICommissionPrompt"
                />

                <!-- AI Call Commission -->
                <SettingCard 
                    icon="phone"
                    title="AI Call Komisyon"
                    :value="`${aiCallCommission}%`"
                    @edit="showAiCallCommissionPrompt"
                />

                <!-- VAT Rate -->
                <SettingCard 
                    icon="calculator"
                    title="KDV Oranı"
                    :value="`${vatRate}%`"
                    @edit="showVatRatePrompt"
                />
            </div>
        </div>

        <!-- Integration Info Section -->
        <div class="settings-section">
            <div class="section-header">
                <a-icon type="link" />
                <h3>Entegrasyon Bilgileri</h3>
            </div>

            <div class="integration-info">
                <!-- Mobile Status -->
                <div class="info-item">
                    <a-icon type="mobile" />
                    <span class="info-label">Mobil</span>
                    <a-badge 
                        :status="site.mobileUserCount > 0 ? 'success' : 'error'"
                        :text="site.mobileUserCount > 0 ? 'Aktif' : 'Pasif'"
                    />
                </div>

                <!-- Website -->
                <div class="info-item">
                    <a-icon type="global" />
                    <span class="info-label">Web Sitesi</span>
                    <a :href="`${IS_DEV_ENVIRONMENT ? 'http' : 'https'}://${site.settings.website}`" 
                       target="_blank"
                       class="info-link">
                        {{ site.settings.website }}
                        <a-icon type="link" />
                    </a>
                </div>

                <!-- Website Origins -->
                <div class="info-item full-width">
                    <a-icon type="deployment-unit" />
                    <span class="info-label">Web Sitesi Origin'leri</span>
                    <div class="origins-list">
                        <div 
                            v-for="(dom, index) in site.integratedOrigins" 
                            :key="index"
                            class="origin-item"
                        >
                            <a :href="`${IS_DEV_ENVIRONMENT ? 'http' : 'https'}://${dom}`" 
                               target="_blank"
                               class="origin-link">
                                <a-icon type="link" />
                                {{ dom }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Danger Zone -->
        <div class="danger-zone">
            <div class="danger-header">
                <a-icon type="warning" />
                <h3>Tehlikeli Bölge</h3>
            </div>
            <a-button 
                type="danger" 
                size="large"
                @click="$emit('delete-site', site)"
            >
                <a-icon type="delete" />
                Bu Site'yi Sil
            </a-button>
        </div>
    </div>
</template>

<script>
import DashboardCard from './components/DashboardCard.vue';
import SettingCard from './components/SettingCard.vue';
import { IS_DEV_ENVIRONMENT } from '../../utils/utils';
import moment from 'moment';
import * as _ from 'lodash';

export default {
    name: 'SiteDetailDashboard',
    components: {
        DashboardCard,
        SettingCard
    },
    props: {
        site: {
            type: Object,
            required: true
        },
        enable: {
            type: Boolean,
            required: true
        },
        suspend: {
            type: Boolean,
            required: true
        },
        blink: Boolean,
        paymentMethod: String,
        trialDay: [Number, String],
        whatsappWallet: Number,
        maxLowBalance: Number,
        gupshupCommission: Number,
        whatsappCommission: Number,
        totalDiscount: Number,
        monthlyMailSendLimit: Number,
        aiUsageCommission: Number,
        aiCallCommission: Number,
        vatRate: Number,
        invoiceDueDays: [Number, String],
        balance: Object
    },
    data() {
        return {
            IS_DEV_ENVIRONMENT
        };
    },
    computed: {
        localEnable: {
            get() { return this.enable; },
            set(value) { this.$emit('update:enable', value); }
        },
        localSuspend: {
            get() { return this.suspend; },
            set(value) { this.$emit('update:suspend', value); }
        },
        localBlink: {
            get() { return this.blink; },
            set(value) { this.$emit('update:blink', value); }
        },
        localPaymentMethod: {
            get() { return this.paymentMethod; },
            set(value) { this.$emit('update:paymentMethod', value); }
        }
    },
    methods: {
        lastActivity() {
            if (this.site.lastLoginTS) return this.$moment(this.site.lastLoginTS).fromNow();
            else return '-';
        },
        getRemainingTrialDay() {
            const createdDate = this.site.createdAt;
            const trialDay = _.get(this.site, 'settings.plan.trialDays');
            if (!isNaN(trialDay)) {
                const diff = moment().diff(moment(createdDate, 'YYYY-MM-DDTHH:mm:ss'), 'days');
                if (diff < trialDay) {
                    return trialDay - diff;
                } else {
                    return 0;
                }
            } else {
                return 0;
            }
        },
        getSuspendReasonText(reason) {
            const reasons = {
                'trial-expired': 'Trial Süresi Doldu',
                'payment': 'Ödeme Yapılmadı',
                'admin-suspend': 'Admin Tarafından Askıya Alındı',
                'other': 'Diğer'
            };
            return reasons[reason] || 'Bilinmiyor';
        },
        showTrialPrompt() {
            this.$emit('show-trial-prompt');
        },
        showTotalDiscountPrompt() {
            this.$emit('show-total-discount-prompt');
        },
        showWhasappWalletPrompt() {
            this.$emit('show-whatsapp-wallet-prompt');
        },
        showWhasappLowLimitPrompt() {
            this.$emit('show-whatsapp-low-limit-prompt');
        },
        showMonthlyMailSendLimitPrompt() {
            this.$emit('show-monthly-mail-send-limit-prompt');
        },
        showGupshupCommissionPrompt() {
            this.$emit('show-gupshup-commission-prompt');
        },
        showWhatsappCommissionPrompt() {
            this.$emit('show-whatsapp-commission-prompt');
        },
        showOpenAICommissionPrompt() {
            this.$emit('show-openai-commission-prompt');
        },
        showAiCallCommissionPrompt() {
            this.$emit('show-ai-call-commission-prompt');
        },
        showVatRatePrompt() {
            this.$emit('show-vat-rate-prompt');
        },
        showInvoiceDueDaysPrompt() {
            this.$emit('show-invoice-due-days-prompt');
        }
    }
};
</script>

<style lang="scss" scoped>
.site-detail-dashboard {
    padding: 24px;
    background: #f5f7fa;

    .dashboard-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 20px;
        margin-bottom: 32px;

        .metric-value {
            font-size: 32px;
            font-weight: 700;
            color: #1890ff;
            margin-top: 8px;

            &.realtime {
                color: #52c41a;
                animation: pulse 2s ease-in-out infinite;
            }
        }

        .metric-text {
            font-size: 16px;
            font-weight: 500;
            color: #595959;
            margin-top: 8px;
        }

        .status-card,
        .suspend-card {
            &.is-suspended {
                border-color: #ff4d4f;
                background: #fff2f0;
            }
        }

        .suspend-reason {
            margin-top: 12px;
        }
    }

    .settings-section {
        background: white;
        border-radius: 8px;
        padding: 24px;
        margin-bottom: 24px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

        .section-header {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-bottom: 20px;
            padding-bottom: 16px;
            border-bottom: 2px solid #f0f0f0;

            .anticon {
                font-size: 24px;
                color: #1890ff;
            }

            h3 {
                margin: 0;
                font-size: 20px;
                font-weight: 600;
                color: #262626;
            }
        }

        .settings-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 16px;
        }

        .payment-method-card {
            grid-column: span 2;
            
            .ant-radio-group {
                display: flex;
                width: 100%;
                gap: 8px;
            }

            .ant-radio-button-wrapper {
                flex: 1;
                text-align: center;
                height: 40px;
                line-height: 38px;
                font-weight: 500;
                transition: all 0.3s ease;

                &:hover {
                    transform: translateY(-1px);
                }

                &.ant-radio-button-wrapper-checked {
                    background: #1890ff;
                    border-color: #1890ff;
                    color: white;
                    box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
                }
            }
        }
    }

    .integration-info {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .info-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px;
            background: #fafafa;
            border-radius: 6px;
            transition: all 0.3s ease;

            &:hover {
                background: #f0f0f0;
            }

            &.full-width {
                flex-direction: column;
                align-items: flex-start;
            }

            .anticon {
                font-size: 20px;
                color: #1890ff;
            }

            .info-label {
                font-weight: 500;
                color: #595959;
                min-width: 120px;
            }

            .info-link {
                color: #1890ff;
                text-decoration: none;
                display: flex;
                align-items: center;
                gap: 6px;
                transition: color 0.3s ease;

                &:hover {
                    color: #40a9ff;
                    text-decoration: underline;
                }
            }

            .origins-list {
                display: flex;
                flex-wrap: wrap;
                gap: 8px;
                width: 100%;

                .origin-item {
                    background: #f0f5ff;
                    border: 1px solid #adc6ff;
                    border-radius: 4px;
                    padding: 6px 12px;
                    transition: all 0.3s ease;

                    &:hover {
                        background: #e6f7ff;
                        border-color: #69c0ff;
                        transform: translateY(-1px);
                        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2);
                    }

                    .origin-link {
                        color: #1890ff;
                        text-decoration: none;
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        font-weight: 500;

                        &:hover {
                            color: #096dd9;
                        }
                    }
                }
            }
        }
    }

    .danger-zone {
        background: white;
        border: 2px solid #ff4d4f;
        border-radius: 8px;
        padding: 24px;
        text-align: center;

        .danger-header {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            margin-bottom: 16px;

            .anticon {
                font-size: 24px;
                color: #ff4d4f;
            }

            h3 {
                margin: 0;
                font-size: 18px;
                font-weight: 600;
                color: #ff4d4f;
            }
        }

        .ant-btn-danger {
            height: 48px;
            font-size: 16px;
            font-weight: 600;
        }
    }
}

@keyframes pulse {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
}
</style>
